/**
 * ProgramLandingPage — ad-focused single-program landing.
 *
 * Design goals (per user request, Feb 2026):
 *  1. ONE program only per URL — no other programs, no menu clutter.
 *  2. Minimal header: clickable logo (returns to homepage) + top CTA button.
 *  3. CTA at TOP (in hero) AND at BOTTOM — both "Foglalás" (free 20-min chat)
 *     and "Megvásárlom" (opens the existing CheckoutModal).
 *  4. Semi-private card uses its 2×2 tier toggle (group × sessions); other
 *     programs render a single tier or a linear tier switch.
 *  5. Meta Pixel: reuses existing standard events fired by BookingModal /
 *     CheckoutModal — no bespoke pixel wiring needed here.
 *
 * URL slugs live in /app/frontend/src/config/programLandings.js. Routes are
 * registered in App.js with short, ad-friendly paths (e.g. /eletmodvalto).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { usePaidAssessment } from '../utils/usePublicConfig';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Check, MapPin, ShieldCheck, Sparkles, Users, Video,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import BookingModal from '../components/BookingModal';
import CheckoutModal from '../components/CheckoutModal';
import { getPrograms } from '../services/api';
import { GYM_VENUE } from '../config/venue';
import { PROGRAM_LANDINGS } from '../config/programLandings';
import { useDocumentMeta } from '../utils/useDocumentMeta';

const fmtHuf = (n) => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const accentClasses = (accent) => {
  if (accent === 'emerald') {
    return {
      badgeBg: 'bg-emerald-600/20 border-emerald-500/30',
      badgeText: 'text-emerald-300',
      accentText: 'text-emerald-400',
      cta: 'bg-emerald-600 hover:bg-emerald-500 text-white',
      ctaOutline: 'border-2 border-emerald-400 text-emerald-300 hover:bg-emerald-500/10',
      priceCard: 'border-emerald-500 bg-emerald-50/60',
      priceAccent: 'text-emerald-700',
      priceBadge: 'bg-emerald-100 text-emerald-800',
      tierActive: 'bg-emerald-600 text-white',
      tierIdle: 'bg-white text-gray-700 hover:bg-emerald-50',
    };
  }
  return {
    badgeBg: 'bg-yellow-600/20 border-yellow-500/30',
    badgeText: 'text-yellow-300',
    accentText: 'text-yellow-400',
    cta: 'bg-yellow-600 hover:bg-yellow-500 text-white',
    ctaOutline: 'border-2 border-yellow-400 text-yellow-300 hover:bg-yellow-500/10',
    priceCard: 'border-yellow-500 bg-yellow-50/60',
    priceAccent: 'text-yellow-700',
    priceBadge: 'bg-yellow-100 text-yellow-800',
    tierActive: 'bg-yellow-600 text-white',
    tierIdle: 'bg-white text-gray-700 hover:bg-yellow-50',
  };
};

/* ------------------------------------------------------------------ */
/* Minimal top nav — logo returns home, a single primary CTA on the   */
/* right. No other nav items so ad clicks don't leak to other pages.  */
/* ------------------------------------------------------------------ */
const MinimalHeader = ({ onBookClick, accent }) => {
  const cls = accentClasses(accent);
  return (
    <header
      data-testid="lp-header"
      className="sticky top-0 z-40 bg-gray-950/85 backdrop-blur-md border-b border-gray-800"
    >
      <div className="container mx-auto px-6 py-3 flex items-center justify-between gap-3">
        <Link
          to="/"
          data-testid="lp-header-logo"
          className="flex items-center gap-2 text-white font-bold text-base sm:text-lg shrink-0"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          <span className="text-yellow-400 tracking-widest">OROSZ</span>
          <span className="text-white">COACHING</span>
        </Link>
        <Button
          data-testid="lp-header-book-btn"
          onClick={onBookClick}
          className={`${cls.cta} font-bold text-xs sm:text-sm md:text-base px-3 sm:px-4 md:px-6 shrink-0 whitespace-nowrap`}
        >
          <span className="sm:hidden">Konzultáció</span>
          <span className="hidden sm:inline">Ingyenes konzultáció</span>
        </Button>
      </div>
    </header>
  );
};

/* ------------------------------------------------------------------ */
/* Semi-private tier UI: two independent toggles (group × sessions).  */
/* Standard programs: a single row of tier labels. Both branches      */
/* update `selectedTierIndex` on the same shared state.               */
/* ------------------------------------------------------------------ */
const TierSelector = ({ program, isSemi, selectedTierIndex, setSelectedTierIndex, accent }) => {
  const cls = accentClasses(accent);
  const tiers = program.tiers || [];

  if (isSemi) {
    const activeTier = tiers[selectedTierIndex] || tiers[0];
    const activeGroup = activeTier?.group_size || 2;
    const activeSessions = activeTier?.sessions || 12;
    const findIdx = (gs, s) => tiers.findIndex(t => t.group_size === gs && t.sessions === s);
    const setGroup = (gs) => { const i = findIdx(gs, activeSessions); if (i >= 0) setSelectedTierIndex(i); };
    const setSessions = (s) => { const i = findIdx(activeGroup, s); if (i >= 0) setSelectedTierIndex(i); };
    return (
      <div className="space-y-2">
        <div data-testid="lp-semi-group-toggle" className="flex gap-1 p-1 bg-gray-100 rounded-lg">
          {[2, 3].map(gs => (
            <button
              key={gs}
              data-testid={`lp-semi-group-${gs}`}
              onClick={() => setGroup(gs)}
              className={`flex-1 px-3 py-2 text-sm font-semibold rounded-md transition-all ${
                activeGroup === gs ? cls.tierActive : cls.tierIdle
              }`}
            >
              {gs === 2 ? 'Duó · 2 fő' : 'Trió · 3 fő'}
            </button>
          ))}
        </div>
        <div data-testid="lp-semi-sessions-toggle" className="flex gap-1 p-1 bg-gray-100 rounded-lg">
          {[8, 12].map(s => (
            <button
              key={s}
              data-testid={`lp-semi-sessions-${s}`}
              onClick={() => setSessions(s)}
              className={`flex-1 px-3 py-2 text-sm font-semibold rounded-md transition-all ${
                activeSessions === s ? cls.tierActive : cls.tierIdle
              }`}
            >
              {s} alkalom
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (tiers.length <= 1) return null;
  return (
    <div data-testid="lp-tier-toggle" className="flex gap-1 p-1 bg-gray-100 rounded-lg">
      {tiers.map((tier, idx) => (
        <button
          key={idx}
          data-testid={`lp-tier-${idx}`}
          onClick={() => setSelectedTierIndex(idx)}
          className={`flex-1 px-3 py-2 text-sm font-semibold rounded-md transition-all ${
            selectedTierIndex === idx ? cls.tierActive : cls.tierIdle
          }`}
        >
          {tier.label}
        </button>
      ))}
    </div>
  );
};

/* ================================================================== */
/* Main page component                                                */
/* ================================================================== */
const ProgramLandingPage = ({ slug }) => {
  const paidAssessment = usePaidAssessment();
  const navigate = useNavigate();
  const landing = PROGRAM_LANDINGS[slug];

  const [programs, setPrograms] = useState(null);
  const [selectedTierIndex, setSelectedTierIndex] = useState(0);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useDocumentMeta({
    title: landing?.metaTitle,
    description: landing?.metaDescription,
  });

  useEffect(() => {
    let cancelled = false;
    getPrograms()
      .then((data) => {
        if (cancelled) return;
        setPrograms(data.programs || []);
      })
      .catch(() => { if (!cancelled) setPrograms([]); });
    return () => { cancelled = true; };
  }, []);

  const program = useMemo(() => {
    if (!programs || !landing) return null;
    return programs.find(landing.programMatcher) || null;
  }, [programs, landing]);

  // Semi-private LP defaults to "Duó · 12 alkalom" — the same default
  // ProgramsSection uses so the "big price" the ad promises matches.
  useEffect(() => {
    if (!program) return;
    const tiers = program.tiers || [];
    if (program.variant === 'semi_private') {
      const idx = tiers.findIndex(t => t.group_size === 2 && t.sessions === 12);
      setSelectedTierIndex(idx >= 0 ? idx : 0);
    } else if (tiers.length > 1) {
      setSelectedTierIndex(1); // 12-alkalom tier by default for standard programs
    } else {
      setSelectedTierIndex(0);
    }
  }, [program]);

  if (!landing) return <Navigate to="/" replace />;

  const cls = accentClasses(landing.accent);
  const isSemi = program?.variant === 'semi_private';
  const activeTier = program?.tiers?.[selectedTierIndex] || program?.tiers?.[0] || null;
  const isOffline = program && program.level && program.level !== 'Online' && program.category !== 'consultation';
  const isOnline = program && program.level === 'Online';

  const scrollToPrice = () => {
    const el = document.getElementById('lp-price');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white">
      <MinimalHeader onBookClick={() => setBookingOpen(true)} accent={landing.accent} />

      {/* ============================================================ */}
      {/* HERO — badge, H1, sub, benefits, CTA row (top CTA lives here) */}
      {/* ============================================================ */}
      <section
        data-testid="lp-hero"
        className="relative overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${landing.heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/95 via-gray-950/90 to-gray-950/70"></div>
        </div>
        <div className="relative z-10 container mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className={`inline-block mb-5 px-4 py-2 border rounded-full ${cls.badgeBg}`}>
              <span className={`font-semibold text-xs tracking-widest uppercase ${cls.badgeText}`}>
                {program?.subtitle || 'Egyéni edzői mentorálás'}
              </span>
            </div>
            <h1
              data-testid="lp-hero-headline"
              className="text-4xl md:text-6xl font-bold text-white mb-5 leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {landing.headline}
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed">
              {landing.subheadline}
            </p>

            {/* Quick benefits (max 5) */}
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 mb-9">
              {landing.benefits.slice(0, 5).map((b, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <Check className={`${cls.accentText} flex-shrink-0 mt-1`} size={18} />
                  <span className="text-sm md:text-base text-gray-200 leading-snug">{b}</span>
                </div>
              ))}
            </div>

            {/* TOP CTA — Booking + Buy Now (both fire the same modals used
                site-wide, so all Meta Pixel standard events are already
                wired through those components). */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="lp-hero-book-btn"
                onClick={() => setBookingOpen(true)}
                size="lg"
                className={`${cls.cta} font-bold text-base md:text-lg px-7 py-6`}
              >
                Ingyenes 20 perces konzultáció
                <ArrowRight className="ml-2" size={18} />
              </Button>
              <Button
                data-testid="lp-hero-buy-btn"
                onClick={scrollToPrice}
                size="lg"
                variant="outline"
                className={`${cls.ctaOutline} bg-transparent font-bold text-base md:text-lg px-7 py-6`}
              >
                Árak megtekintése
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* PROGRAM DETAILS — full feature list + venue / trust badges    */}
      {/* ============================================================ */}
      {program && (
        <section data-testid="lp-details" className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              {(isOffline || isOnline) && (
                <div className="flex flex-wrap gap-3 mb-8">
                  {isOffline && (
                    <div data-testid="lp-venue" className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-full text-xs font-semibold text-gray-700">
                      <MapPin size={13} className="text-yellow-600" />
                      Helyszín: <span className="text-gray-900">{GYM_VENUE.name}</span>
                    </div>
                  )}
                  {isOnline && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-full text-xs font-semibold text-gray-700">
                      <Video size={13} className="text-yellow-600" />
                      Online — bárhonnan
                    </div>
                  )}
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-full text-xs font-semibold text-gray-700">
                    <ShieldCheck size={13} className="text-yellow-600" />
                    Tudományosan megalapozott felépítés
                  </div>
                  {!paidAssessment && (
                    <div
                      data-testid="lp-free-assessment-chip"
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 rounded-full text-xs font-bold text-white"
                    >
                      <ShieldCheck size={13} />
                      Ingyenes állapotfelmérés
                    </div>
                  )}
                  {isSemi && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-full text-xs font-semibold text-gray-700">
                      <Users size={13} className="text-yellow-600" />
                      Max. 3 fős zárt csoport
                    </div>
                  )}
                </div>
              )}

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {program.title}
              </h2>

              {program.audience && (
                <div className="mb-4 text-base">
                  <span className="font-semibold text-gray-900">Kiknek szól? </span>
                  <span className="text-gray-700">{program.audience}</span>
                </div>
              )}

              {program.focus && (
                <div className="mb-8 text-base">
                  <span className="font-semibold text-gray-900">Fókusz: </span>
                  <span className="text-gray-700">{program.focus}</span>
                </div>
              )}

              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 mt-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Mit tartalmaz a program?
              </h3>
              <div className="space-y-3 mb-8">
                {(program.features || []).map((f, i) => {
                  const isTracker = f === 'Beépített haladáskövető és heti check-in a fiókodban';
                  return (
                    <div key={i} className="flex items-start gap-3">
                      {isTracker
                        ? <Sparkles className="text-yellow-500 flex-shrink-0 mt-1" size={20} />
                        : <Check className={`${cls.accentText.replace('400', '600')} flex-shrink-0 mt-1`} size={20} />}
                      <span className={`text-base ${isTracker ? 'text-gray-900 font-semibold' : 'text-gray-700'}`}>{f}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============================================================ */}
      {/* PRICE — tier toggle + big price + inline CTA                  */}
      {/* ============================================================ */}
      {program && (
        <section
          id="lp-price"
          data-testid="lp-price"
          className="py-16 md:py-20 bg-gray-50 border-y border-gray-200"
        >
          <div className="container mx-auto px-6">
            <div className={`max-w-2xl mx-auto rounded-2xl border-2 ${cls.priceCard} p-6 md:p-10 shadow-sm`}>
              <div className="text-center mb-6">
                <div className={`inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${cls.priceBadge}`}>
                  {isSemi ? 'Havi bérlet · Kiscsoport' : 'Havi díj'}
                </div>
                <h3 className="mt-3 text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Válaszd ki a saját csomagod
                </h3>
              </div>

              {(program.tiers || []).length > 1 && (
                <div className="mb-6">
                  <TierSelector
                    program={program}
                    isSemi={isSemi}
                    selectedTierIndex={selectedTierIndex}
                    setSelectedTierIndex={setSelectedTierIndex}
                    accent={landing.accent}
                  />
                </div>
              )}

              {activeTier && (
                <div className="text-center py-4">
                  <div
                    data-testid="lp-price-value"
                    className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {activeTier.price}
                    {isSemi && (
                      <span className="text-2xl md:text-3xl text-gray-700 font-bold">
                        {' · '}{activeTier.group_size} fő együtt
                      </span>
                    )}
                  </div>

                  {isSemi && activeTier.per_person_per_session && (
                    <div className="mt-3">
                      <span className={`inline-flex items-baseline gap-1.5 ${cls.priceAccent} font-bold`} style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        <span className="text-gray-500 font-normal text-sm">alkalomra vetítve</span>
                        <span className="text-xl">{fmtHuf(activeTier.per_person_per_session)} Ft / fő</span>
                      </span>
                    </div>
                  )}
                  {isSemi && (
                    <div className="text-sm text-gray-500 mt-2">
                      {activeTier.group_size} fő × {activeTier.sessions} alkalom / hó
                    </div>
                  )}
                  {!isSemi && activeTier.label && (
                    <div className="text-sm text-gray-500 mt-2">{activeTier.label}</div>
                  )}
                  {!paidAssessment && (
                    <div
                      data-testid="lp-free-assessment-badge"
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-white text-xs font-extrabold uppercase tracking-wide shadow-sm"
                    >
                      <ShieldCheck size={15} strokeWidth={2.6} />
                      Ingyenes állapotfelmérés · 0 Ft
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6 flex flex-col gap-3">
                <Button
                  data-testid="lp-price-buy-btn"
                  onClick={() => setCheckoutOpen(true)}
                  size="lg"
                  className={`w-full ${cls.cta} font-bold text-base md:text-lg py-6`}
                >
                  Megveszem most
                  <ArrowRight className="ml-2" size={18} />
                </Button>
                <Button
                  data-testid="lp-price-book-btn"
                  onClick={() => setBookingOpen(true)}
                  variant="outline"
                  className="w-full font-semibold border-gray-300 text-gray-700 hover:bg-white"
                >
                  Ingyenes 20 perces konzultáció
                </Button>
                <p className="text-[11px] text-gray-500 text-center leading-snug -mt-0.5">
                  Kötelezettség nélküli beszélgetés — átbeszéljük a célod és a lehetőségeket.
                </p>
              </div>

              {isOffline && (
                paidAssessment ? (
                  <div className="mt-6 p-4 bg-white/70 border border-gray-200 rounded-lg text-xs text-gray-600 leading-relaxed">
                    <span className="font-semibold text-gray-800">Részletes állapotfelmérés (0. alkalom):</span>{' '}
                    A részletes felmérés és kérdőíves anamnézis díja <span className="font-bold">10.000 Ft</span>,
                    amelyet bérletvásárlás esetén <span className="font-bold">teljes egészében jóváírunk</span> —
                    a helyszíni találkozó alkalmával készpénzben rendezhető.
                  </div>
                ) : (
                  <div
                    data-testid="lp-free-assessment-note"
                    className="mt-6 p-4 bg-emerald-600 rounded-lg text-xs text-white leading-relaxed shadow-sm"
                  >
                    <span className="block text-sm font-extrabold uppercase tracking-wide mb-1">
                      Ingyenes állapotfelmérés
                    </span>
                    A részletes mozgásszervi állapotfelmérés és kérdőíves anamnézis (0. alkalom){' '}
                    <span className="font-extrabold">teljesen díjmentes</span> — bérletvásárlás nélkül is.
                  </div>
                )
              )}
              {isOnline && (
                paidAssessment ? (
                  <div className="mt-6 p-4 bg-white/70 border border-gray-200 rounded-lg text-xs text-gray-600 leading-relaxed">
                    <span className="font-semibold text-gray-800">Részletes felmérés + anamnézis (0. alkalom):</span>{' '}
                    Díja <span className="font-bold">10.000 Ft</span>, amelyet bérletvásárlás esetén{' '}
                    <span className="font-bold">teljes egészében jóváírunk</span>. Önmagában, kártyás fizetéssel is elérhető.
                  </div>
                ) : (
                  <div
                    data-testid="lp-free-assessment-note"
                    className="mt-6 p-4 bg-yellow-500 rounded-lg text-xs text-gray-900 leading-relaxed shadow-sm"
                  >
                    <span className="block text-sm font-extrabold uppercase tracking-wide mb-1">
                      Ingyenes állapotfelmérés
                    </span>
                    A részletes felmérés és kérdőíves anamnézis (0. alkalom){' '}
                    <span className="font-extrabold">díjmentes</span> — nem kell érte fizetned.
                  </div>
                )
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================ */}
      {/* BOTTOM CTA — the second-chance conversion block               */}
      {/* ============================================================ */}
      <section data-testid="lp-bottom-cta" className="relative overflow-hidden bg-gray-950">
        <div className="container mx-auto px-6 py-16 md:py-24 text-center">
          <div className={`inline-block mb-4 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${cls.priceBadge}`}>
            Kezdd itt
          </div>
          <h2
            data-testid="lp-bottom-headline"
            className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Kezdjük el <span className={cls.accentText}>közösen</span> a következő fejezetet
          </h2>
          <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto mb-8">
            Foglalj egy ingyenes, 20 perces kötelezettség nélküli beszélgetést (személyesen a BB Gymben vagy online, ahogy neked kényelmes), ahol átbeszéljük a célod és a lehetőségeket — vagy vásárold meg most a programod és kezdd el a jövő héten.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              data-testid="lp-bottom-book-btn"
              onClick={() => setBookingOpen(true)}
              size="lg"
              className={`${cls.cta} font-bold text-base md:text-lg px-8 py-6`}
            >
              Ingyenes 20 perces konzultáció
              <ArrowRight className="ml-2" size={18} />
            </Button>
            <Button
              data-testid="lp-bottom-buy-btn"
              onClick={() => setCheckoutOpen(true)}
              size="lg"
              variant="outline"
              className={`${cls.ctaOutline} bg-transparent font-bold text-base md:text-lg px-8 py-6`}
            >
              Megveszem most
            </Button>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* MINIMAL FOOTER — only legal links, no site navigation          */}
      {/* ============================================================ */}
      <footer className="bg-gray-950 border-t border-gray-800 py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
            <div>© {new Date().getFullYear()} Orosz Coaching · Orosz Gábor E.V.</div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center">
              <button type="button" onClick={() => navigate('/aszf')} className="hover:text-yellow-400 transition-colors">ÁSZF</button>
              <button type="button" onClick={() => navigate('/adatvedelem')} className="hover:text-yellow-400 transition-colors">Adatvédelem</button>
              <button type="button" onClick={() => navigate('/impresszum')} className="hover:text-yellow-400 transition-colors">Impresszum</button>
              <button type="button" onClick={() => navigate('/elallasi-nyilatkozat')} className="hover:text-yellow-400 transition-colors">Elállás</button>
            </div>
          </div>
        </div>
      </footer>

      <BookingModal
        isOpen={bookingOpen}
        onClose={() => setBookingOpen(false)}
      />
      <CheckoutModal
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        program={program}
        tierIndex={selectedTierIndex}
      />
    </div>
  );
};

export default ProgramLandingPage;
